import {
  ApplicationRef,
  createComponent,
  DOCUMENT,
  EnvironmentInjector,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';

import { ModalBottomSheetContainerComponent } from './modal-bottom-sheet-container.component';
import { ModalBottomSheetRef } from './modal-bottom-sheet-ref';

export const MODAL_BOTTOM_SHEET_DATA = new InjectionToken<unknown>(
  'MODAL_BOTTOM_SHEET_DATA',
);

export interface ModalBottomSheetConfig<TData = unknown> {
  data?: TData;
  injector?: Injector;
  trapFocus?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ModalBottomSheetService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);

  open<TComponent, TResult = unknown>(
    component: Type<TComponent>,
    config?: ModalBottomSheetConfig,
  ): ModalBottomSheetRef<TResult> {
    const hostElement = this.document.createElement('div');
    this.document.body.appendChild(hostElement);

    const containerRef = createComponent(ModalBottomSheetContainerComponent, {
      environmentInjector: this.environmentInjector,
      hostElement,
    });
    containerRef.setInput('trapFocus', config?.trapFocus ?? true);
    this.applicationRef.attachView(containerRef.hostView);
    containerRef.changeDetectorRef.detectChanges();

    let dismissResult: TResult | undefined;
    const sheetRef = new ModalBottomSheetRef<TResult>((result) => {
      dismissResult = result;
      containerRef.instance.close();
    });

    containerRef.instance.dismissed.subscribe(() => {
      this.applicationRef.detachView(containerRef.hostView);
      containerRef.destroy();
      hostElement.remove();
      sheetRef.handleDismissed(dismissResult);
    });

    containerRef.instance.attachContent(
      component,
      Injector.create({
        providers: [
          { provide: ModalBottomSheetRef, useValue: sheetRef },
          { provide: MODAL_BOTTOM_SHEET_DATA, useValue: config?.data ?? null },
        ],
        parent: config?.injector ?? this.environmentInjector,
      }),
    );
    containerRef.instance.open();

    return sheetRef;
  }
}
